import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { deliveriesApi } from '@/api/deliveries.api';
import type { DeliveryDetail, DeliveryJob } from '@/api/types';
import { useAuth } from '@/auth/auth-context';
import { RUN_SHEET_POLL_MS } from '@/lib/env';
import { queryKeys } from '@/lib/query-keys';
import { inWorkingOrder, isCarrying, isLive } from '@/lib/status';

/**
 * Every job this rider is carrying that has not finished, in the order they
 * will work it.
 *
 * Polled, because there are no push notifications yet. Dispatch assigns from
 * the admin panel, and without this a rider would only discover a new job by
 * pulling down on a list they have no reason to think has changed.
 */
export function useRunSheet() {
  const { isSignedIn } = useAuth();

  const query = useQuery({
    queryKey: queryKeys.runSheet,
    enabled: isSignedIn,
    queryFn: () => deliveriesApi.list({ activeOnly: true }),
    refetchInterval: RUN_SHEET_POLL_MS,
    /*
     * The poll keeps running with the tab in the background: a rider switches
     * to Maps for the whole leg and comes back expecting the sheet to be
     * current, not to start refreshing at the moment they look at it.
     */
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const jobs = useMemo(() => inWorkingOrder(query.data ?? []), [query.data]);

  return {
    ...query,
    jobs,
    /**
     * A rider carries one bag: whatever is furthest along is what they are
     * doing, and everything below it on the sheet is queued behind it.
     */
    activeJob: jobs[0] ?? null,
    /** Whether they are physically holding somebody's medicine right now. */
    isCarrying: isCarrying(jobs),
  };
}

/**
 * What this rider has finished — delivered and not delivered together.
 *
 * Both, deliberately. A list of successes is a scoreboard; a rider looking
 * back is usually trying to find the one that went wrong, and hiding failures
 * makes the only useful question impossible to ask.
 */
export function useHistory() {
  const { isSignedIn } = useAuth();

  return useQuery<DeliveryJob[]>({
    queryKey: queryKeys.history,
    enabled: isSignedIn,
    queryFn: async () => {
      const jobs = await deliveriesApi.list({ limit: 50 });
      return jobs.filter((job) => !isLive(job));
    },
    staleTime: 60_000,
  });
}

export function useDelivery(deliveryId: string) {
  const { isSignedIn } = useAuth();

  return useQuery<DeliveryDetail>({
    queryKey: queryKeys.delivery(deliveryId),
    enabled: isSignedIn && Boolean(deliveryId),
    queryFn: () => deliveriesApi.get(deliveryId),
    staleTime: 10_000,
  });
}

/**
 * Everything that has to be told after a delivery moves.
 *
 * Three caches read off one transition and they go stale together: the run
 * sheet drives the ordering and the tab badge, the profile carries the
 * server-side BUSY/AVAILABLE flip, and the delivery itself is what is on
 * screen. Written once here so a screen cannot remember two of the three.
 */
export function useDeliveryCache() {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      /** Redraw from the response a transition already returned. */
      write(delivery: DeliveryDetail) {
        queryClient.setQueryData(queryKeys.delivery(delivery.id), delivery);
        void queryClient.invalidateQueries({ queryKey: queryKeys.runSheet });
      },

      /** The job moved underneath us, or finished. Ask the server again. */
      invalidate(deliveryId?: string) {
        if (deliveryId) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.delivery(deliveryId) });
        }
        void queryClient.invalidateQueries({ queryKey: queryKeys.runSheet });
        void queryClient.invalidateQueries({ queryKey: queryKeys.riderProfile });
      },

      /** A finished job leaves the run sheet and joins the history. */
      settled(deliveryId: string) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.delivery(deliveryId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.runSheet });
        void queryClient.invalidateQueries({ queryKey: queryKeys.history });
        void queryClient.invalidateQueries({ queryKey: queryKeys.riderProfile });
        void queryClient.invalidateQueries({ queryKey: queryKeys.cashBalance });
      },
    }),
    [queryClient],
  );
}
